import { IBalanceSheet } from "api/accountingService";

/**
 * Wyprowadza bilans (aktywa/pasywa) z sald kont (plik BO/ZOiS).
 *
 * Kluczowe obserwacje z realnego ZOiS klienta („…kont analitycznych wszystkich"):
 *  - plik prowadzi RÓWNOLEGŁE osie analityczne (np. 241-2-1-3 wg typu ORAZ wg
 *    kontrahenta) — sumowanie liści potraja kwoty. Prawdę niesie poziom SYNTETYKI,
 *    gdzie saldo syntetyki = Σ jej analityk (Σ syntetyk = „Razem" ze stopki),
 *  - plik dubluje syntetyki w drugim układzie numeracji o TEJ SAMEJ nazwie
 *    (030≡031, 077≡078, 144≡145, 240≡241 — salda potrafią różnić się o grosze),
 *  - konta pozabilansowe (zespół 9) są zerowe/statystyczne.
 *
 * Reguły:
 *  - liczymy WYŁĄCZNIE syntetyki (kod bez „-" i bez „/"),
 *  - deduplikujemy syntetyki o tej samej NAZWIE (zostaje najmniejszy kod),
 *  - pomijamy zespół 9,
 *  - klasyfikacja: 0→aktywa trwałe (netto, z umorzeniami); 1/2/3/6→saldo Wn aktywa
 *    obrotowe / saldo Ma zobowiązania; 800/810/813/820→kapitał własny;
 *    830/831/840→rezerwy i RMP (pasywa); 860/870 oraz 4/5/7→wynik finansowy.
 *
 * Na danych klienta daje KONTROLĘ (aktywa − pasywa) = 0,00.
 */

export interface IBoAccount {
  code: string;
  name?: string;
  boWn: number;
  boMa: number;
}

const r2 = (v: number) => Math.round(v * 100) / 100;

type Bucket = "FA" | "CA_or_L" | "EQ" | "LIAB" | "NR" | "EXCL";

const classify = (code: string): Bucket => {
  const d = code.match(/\d/)?.[0] ?? "";
  if (d === "9") return "EXCL";
  if (d === "0") return "FA";
  if (d === "1" || d === "2" || d === "3" || d === "6") return "CA_or_L";
  if (d === "4" || d === "5" || d === "7") return "NR";
  if (d === "8") {
    if (code === "830" || code === "831" || code === "840") return "LIAB";
    if (code === "860" || code === "870") return "NR";
    return "EQ"; // 800/810/813/820
  }
  return "CA_or_L";
};

export const deriveBalanceFromAccounts = (
  entries: IBoAccount[],
  year: number,
): IBalanceSheet[] => {
  if (!entries || entries.length === 0) return [];

  // Tylko syntetyki: kod bez „-" (analityka) i bez „/" (numeracja slashowa).
  // Dedup po kodzie na wypadek powtórek w pliku.
  const byCode = new Map<string, IBoAccount>();
  for (const e of entries) {
    if (e.code && !e.code.includes("-") && !e.code.includes("/") && !byCode.has(e.code)) {
      byCode.set(e.code, e);
    }
  }
  const synthetics = [...byCode.values()];

  // Deduplikacja po NAZWIE syntetyki: równoległy układ dubluje konta o tej samej
  // nazwie (salda mogą różnić się o grosze) — zostaje najmniejszy kod.
  const excluded = new Set<string>();
  const byName = new Map<string, string[]>();
  for (const e of synthetics) {
    const nm = (e.name ?? "").trim().toLowerCase();
    if (!nm) continue;
    const arr = byName.get(nm) ?? [];
    arr.push(e.code);
    byName.set(nm, arr);
  }
  for (const codes of byName.values()) {
    if (codes.length < 2) continue;
    const sorted = [...codes].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    for (const c of sorted.slice(1)) excluded.add(c);
  }

  let fixedAssets = 0;
  let currentAssets = 0;
  let equity = 0;
  let liabilities = 0;
  let netResult = 0;

  for (const e of synthetics) {
    if (excluded.has(e.code)) continue;
    const net = (e.boWn || 0) - (e.boMa || 0);
    switch (classify(e.code)) {
      case "EXCL":
        break;
      case "FA":
        fixedAssets += net;
        break;
      case "CA_or_L":
        if (net >= 0) currentAssets += net;
        else liabilities += -net;
        break;
      case "EQ":
        equity += -net;
        break;
      case "LIAB":
        liabilities += -net;
        break;
      case "NR":
        netResult += -net;
        break;
    }
  }

  const totalAssets = r2(fixedAssets + currentAssets);
  const totalLiabilities = r2(equity + liabilities + netResult);

  return [
    {
      periodType: "Annual",
      year,
      month: 0,
      label: "Bilans otwarcia",
      fixedAssets: r2(fixedAssets),
      currentAssets: r2(currentAssets),
      totalAssets,
      equity: r2(equity),
      liabilities: r2(liabilities),
      totalLiabilities,
      netResult: r2(netResult),
    },
  ];
};
